// Every request/response shape the console relies on, as zod schemas (zod 4), matching the REAL
// services: crates/api (src/dto.rs, src/error.rs) and crates/region-api (src/lib.rs, which returns
// crates/query/src/shape.rs results verbatim). One file to diff when a contract moves.
import { z } from "zod";
import type { QueryResponse } from "./ir";

const id = z.string().min(1);
const datetime = z.iso.datetime({ offset: true });
const nullableDatetime = datetime.nullable();

export const REGION_CODES = ["eu", "me", "us", "pk"] as const;
export const ENVIRONMENTS = ["production", "preview", "development"] as const;

export const RegionCode = z.enum(REGION_CODES);
export type RegionCode = z.infer<typeof RegionCode>;
export const EnvironmentName = z.enum(ENVIRONMENTS);
export type EnvironmentName = z.infer<typeof EnvironmentName>;

// ── Errors ───────────────────────────────────────────────────────────────────
/**
 * Error envelope of both APIs: `{"error": {"code", "message"}}`. The control plane has no per-field
 * errors — validation failures are `code: "validation"` with a message that starts with the field
 * name (see `fieldsFor` in http.ts). region-api adds `path` (JSON Pointer into the IR) to `invalid_query`.
 */
export const ApiErrorBody = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    path: z.string().optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

// ── Auth / me ────────────────────────────────────────────────────────────────
export const User = z.object({
  id,
  email: z.email(),
  name: z.string(),
  email_verified: z.boolean(),
  created_at: datetime,
});
export type User = z.infer<typeof User>;

export const OrgRole = z.enum(["owner", "admin", "member", "viewer"]);
export const OrgSummary = z.object({ id, name: z.string(), slug: z.string(), role: OrgRole, created_at: datetime });
export type OrgSummary = z.infer<typeof OrgSummary>;

/** GET /app/me (401 when signed out) and the body of signup/login. */
export const Me = z.object({ user: User, orgs: z.array(OrgSummary) });
export type Me = z.infer<typeof Me>;

export const SignUpInput = z.object({
  name: z.string().trim().min(1, "Enter your name").max(100),
  email: z.email("Enter a valid email address"),
  password: z.string().min(10, "Use at least 10 characters").max(200),
});
export type SignUpInput = z.infer<typeof SignUpInput>;

export const SignInInput = z.object({
  email: z.email("Enter a valid email address"),
  password: z.string().min(1, "Enter your password"),
});
export type SignInInput = z.infer<typeof SignInInput>;

// ── Orgs ─────────────────────────────────────────────────────────────────────
/** POST /app/orgs → the org, with your role in it. */
export const Org = OrgSummary;
export type Org = z.infer<typeof Org>;

export const CreateOrgInput = z.object({
  name: z.string().trim().min(2, "Use at least 2 characters").max(100),
});
export type CreateOrgInput = z.infer<typeof CreateOrgInput>;

// ── Regions ──────────────────────────────────────────────────────────────────
/** GET /app/regions — the region registry (core.regions). */
export const Region = z.object({
  code: RegionCode,
  name: z.string(),
  status: z.enum(["live", "coming_soon"]),
  api_host: z.string().nullable(),
  ingest_host: z.string().nullable(),
});
export type Region = z.infer<typeof Region>;

// ── Projects, environments, keys ─────────────────────────────────────────────
export const Environment = z.object({ id, name: EnvironmentName, is_default: z.boolean() });
export type Environment = z.infer<typeof Environment>;

export const Project = z.object({
  id,
  /** Numeric id the data plane uses (ClickHouse `project_id`). */
  data_id: z.number().int(),
  org_id: id,
  name: z.string(),
  slug: z.string(),
  region: RegionCode,
  timezone: z.string(),
  currency: z.string().length(3),
  retention_days: z.number().int(),
  created_at: datetime,
  /** Your effective role in this project. */
  role: z.string(),
  environments: z.array(Environment),
});
export type Project = z.infer<typeof Project>;

export const CreateProjectInput = z.object({
  name: z.string().trim().min(2, "Use at least 2 characters").max(100),
  region: RegionCode,
  timezone: z.string().min(1),
});
export type CreateProjectInput = z.infer<typeof CreateProjectInput>;

export const PUBLIC_KEY_RE = /^atx_pk_(eu|me|us|pk)_(production|preview|development)_[A-Za-z0-9]{22}$/;

export const PublicKey = z.object({
  id,
  project_id: id,
  environment: EnvironmentName,
  key: z.string().regex(PUBLIC_KEY_RE),
  allowed_origins: z.array(z.string()),
  allowed_bundle_ids: z.array(z.string()),
  created_at: datetime,
  revoked_at: nullableDatetime,
  /** Whether the region's capture service already has this key's latest state. */
  registry_synced: z.boolean(),
});
export type PublicKey = z.infer<typeof PublicKey>;

/** POST /app/orgs/{org}/projects — creating a project mints one public key per environment. */
export const CreateProjectResponse = z.object({ project: Project, public_keys: z.array(PublicKey) });
export type CreateProjectResponse = z.infer<typeof CreateProjectResponse>;

/**
 * POST /app/projects/{id}/keys/{key}/rotate `{keep_old?}` — a replacement for the same environment.
 * With `keep_old` the old key keeps working and `revoked.revoked_at` is still null.
 */
export const RotateKeyResponse = z.object({ revoked: PublicKey, created: PublicKey });
export type RotateKeyResponse = z.infer<typeof RotateKeyResponse>;

// ── Personal access tokens ───────────────────────────────────────────────────
export const SCOPE_DOMAINS = [
  "query",
  "insights",
  "dashboards",
  "cohorts",
  "catalog",
  "flags",
  "experiments",
  "replays",
  "persons",
  "alerts",
  "ai",
  "annotations",
  "usage",
] as const;
/** `domain:read` / `domain:write`, or a named scope (`sql.raw`, …). */
export const Scope = z.string().regex(/^[a-z_]+([.:][a-z_]+)*$/);

export const PersonalToken = z.object({
  id,
  kind: z.string(),
  org_id: id,
  name: z.string(),
  /** First chars of the token, safe to display: `atx_pat_` + 12-char lookup prefix. */
  prefix: z.string(),
  scopes: z.array(Scope),
  /** null = every project in the org. */
  project_ids: z.array(id).nullable(),
  created_at: datetime,
  expires_at: nullableDatetime,
  last_used_at: nullableDatetime,
  revoked_at: nullableDatetime,
});
export type PersonalToken = z.infer<typeof PersonalToken>;

export const CreateTokenInput = z.object({
  name: z.string().trim().min(1, "Name the token").max(100),
  scopes: z.array(Scope).min(1, "Pick at least one scope"),
  expires_in_days: z.number().int().positive().max(365).nullable(),
  org_id: id,
});
export type CreateTokenInput = z.infer<typeof CreateTokenInput>;

/** The secret is returned exactly once, on creation. */
export const CreatedToken = z.object({
  token: PersonalToken,
  secret: z.string().regex(/^atx_pat_[A-Za-z0-9]{44}$/),
});
export type CreatedToken = z.infer<typeof CreatedToken>;

// ── Region token ─────────────────────────────────────────────────────────────
/** POST /app/projects/{id}/region-token `{environment?, scopes?}` — a ≤15 min EdDSA JWT for the region API. */
export const RegionToken = z.object({
  token: z.string().min(1),
  region_api_url: z.url(),
  expires_at: datetime,
  region: RegionCode,
  environment: EnvironmentName,
  scopes: z.array(z.string()),
});
export type RegionToken = z.infer<typeof RegionToken>;

// ── Region API: schema ───────────────────────────────────────────────────────
/** GET {region}/schema/events[?days=] — event names with volume (event_counts_daily MV, bots excluded). */
export const EventsWire = z.object({
  days: z.number().int(),
  events: z.array(z.object({ event: z.string(), count: z.number(), last_seen: z.string() })),
  cached: z.boolean(),
});
export type EventsWire = z.infer<typeof EventsWire>;

const WireType = z.enum(["String", "Numeric", "Boolean", "Null", "Object"]);
/** GET {region}/schema/properties[?event=&type=event|person] — keys with an inferred JSON type. */
export const PropertiesWire = z.object({
  event: z.string().nullable(),
  type: z.enum(["event", "person"]),
  sample: z.number().int(),
  properties: z.array(z.object({ key: z.string(), type: WireType, count: z.number() })),
  cached: z.boolean(),
});
export type PropertiesWire = z.infer<typeof PropertiesWire>;

/** Console view of an event name. */
export interface EventDefinition {
  name: string;
  /** Volume over the schema window (30 days). */
  volume_30d: number;
  /** Last day seen (`YYYY-MM-DD`). */
  last_seen: string | null;
  /** `$`-prefixed events are captured automatically by the SDKs. */
  is_system: boolean;
}

export type PropertyType = "string" | "number" | "boolean" | "object" | "null";
/** Console view of a property key. */
export interface PropertyDefinition {
  name: string;
  type: PropertyType;
  scope: "event" | "person";
  is_system: boolean;
}

export const toEventDefinitions = (w: EventsWire): EventDefinition[] =>
  w.events.map((e) => ({
    name: e.event,
    volume_30d: e.count,
    last_seen: e.last_seen || null,
    is_system: e.event.startsWith("$"),
  }));

const PROPERTY_TYPE: Record<z.infer<typeof WireType>, PropertyType> = {
  String: "string",
  Numeric: "number",
  Boolean: "boolean",
  Object: "object",
  Null: "null",
};

export const toPropertyDefinitions = (w: PropertiesWire): PropertyDefinition[] =>
  w.properties.map((p) => ({
    name: p.key,
    type: PROPERTY_TYPE[p.type],
    scope: w.type,
    is_system: p.key.startsWith("$"),
  }));

// ── Region API: query results (ir.ts result types / crates/query shape.rs) ───
const num = z.number();
const maybe = num.nullable();
const funnelStep = z.object({
  index: z.number().int(),
  event: z.string(),
  label: z.string(),
  count: num,
  conversion_from_first: maybe,
  conversion_from_previous: maybe,
  drop_off: num,
  drop_off_rate: maybe,
});
const period = z.enum(["day", "week", "month"]);

export const QueryResult = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("trends"),
    interval: z.enum(["hour", "day", "week", "month"]),
    timezone: z.string(),
    labels: z.array(z.string()),
    previous_labels: z.array(z.string()).nullable(),
    series: z.array(
      z.object({
        letter: z.string(),
        label: z.string(),
        event: z.string().nullable(),
        math: z.string(),
        breakdown_value: z.string().nullable(),
        compare: z.string().nullable(),
        data: z.array(maybe),
        total: num,
      }),
    ),
    sampling: maybe,
    fast_path: z.boolean(),
  }),
  z.object({
    kind: z.literal("funnel"),
    steps: z.array(funnelStep),
    breakdowns: z.array(z.object({ breakdown_value: z.string(), steps: z.array(funnelStep) })).optional(),
    conversion_window_seconds: num,
    sampling: maybe,
    time_to_convert: z
      .object({
        from_step: z.number().int(),
        to_step: z.number().int(),
        conversions: num,
        average_seconds: maybe,
        median_seconds: maybe,
        p90_seconds: maybe,
        sampled: z.boolean(),
        bins: z.array(z.object({ from_seconds: num, to_seconds: num, count: num })),
      })
      .optional(),
  }),
  z.object({
    kind: z.literal("retention"),
    period,
    periods: z.number().int(),
    retention_type: z.enum(["first_time", "recurring"]),
    cohorts: z.array(z.object({ cohort: z.string(), size: num, values: z.array(maybe), percentages: z.array(maybe) })),
    sampling: maybe,
  }),
  z.object({
    kind: z.literal("paths"),
    node_type: z.enum(["pageview", "screen", "event"]),
    edges: z.array(z.object({ step: z.number().int(), source: z.string(), target: z.string(), value: num })),
    nodes: z.array(z.object({ step: z.number().int(), name: z.string(), outgoing: num })),
    sampling: maybe,
  }),
  z.object({
    kind: z.literal("lifecycle"),
    interval: period,
    labels: z.array(z.string()),
    series: z.array(z.object({ status: z.enum(["new", "returning", "resurrecting", "dormant"]), data: z.array(num) })),
    sampling: maybe,
  }),
  z.object({
    kind: z.literal("stickiness"),
    interval: period,
    labels: z.array(z.string()),
    series: z.array(
      z.object({ letter: z.string(), label: z.string(), event: z.string().nullable(), data: z.array(num), total: num }),
    ),
    sampling: maybe,
  }),
]);

export const QueryMeta = z.object({
  cached: z.boolean(),
  query_fingerprint: z.string(),
  data_version: z.string(),
  date_from: z.string(),
  date_to: z.string(),
  executed_at: z.string(),
  rows_read: num,
  bytes_read: num,
  elapsed_ms: num,
  compiler_version: z.union([z.string(), num]),
});

/** POST {region}/query body `{ query: QueryIr }` → this. */
export const QueryResponseSchema = z.object({
  kind: z.enum(["trends", "funnel", "retention", "paths", "lifecycle", "stickiness"]),
  result: QueryResult,
  meta: QueryMeta,
  compiled: z.object({ sql: z.string(), params: z.unknown() }).optional(),
}) as unknown as z.ZodType<QueryResponse>;
